import CommonTable from "components/ReactTable/Table";
import React, { useState, useEffect, useRef } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  fixedWidthCustomStyle,
  JOB_STATUS_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
  WORK_ORDER,
  SMALL_CASE_WORK_ORDER,
  CAP_CASE_WORK_ORDER,
  getJobStatusStyle,
} from "components/Common/constants";
import {
  formatDateLocal,
  ISOToHumanReadableDate,
} from "components/Common/commonFunctions";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector, useDispatch } from "react-redux";
import { getJobList } from "actions/job";
import { jobList } from "constants/job";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import NotificationAlert from "react-notification-alert";
import { getRegionList } from "actions/job";
import { getJobTypeList } from "actions/job";
import { regionList } from "constants/job";
import { jobTypeList } from "constants/jobType";

const jobColor = {
  JOB_ALLOCATED: "job-allocated",
  REQUEST_APPROVAL: "request-approval",
  PART_REQUESTS: "request-parts",
  REPAIR: "job-repair",
  REPAIRED: "job-repaired",
  QUOTATION_IN_PROGRESS: "quotation-inprogress",
  "Invoice (Not Paid)": "invoice-not-paid",
  "Invoice (Paid)": "invoice-paid",
  CANCELLED: "job-cancelled",
  JOB_CREATED: "job-created",
};

function JobsListing() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);
  const jobListData = useSelector((state) => state.job.jobList);
  const regionListData = useSelector((state) => state.job.regionList);
  const jobTypeListData = useSelector((state) => state.jobType.jobTypeList);

  const [pageNum, setPageNum] = useState(1);
  const formattedJobList = jobListData?.data?.job?.map((item, index) => {
    return {
      ...item,
      srNo:
        (pageNum - 1) *
        (jobListData?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    };
  });
  const [scheduleDateRange, setScheduleDateRange] = useState([null, null]);
  const [jobListState, setJobListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnSearch: false,
  });
  const [searchState, setSearchState] = useState({
    jobNo: "",
    status: null,
    region: null,
    jobType: null,
    filterParams: [],
    isSearched: false,
  });
  const [isDownloading, setIsDownloading] = useState(false);

  //get job list data
  useEffect(() => {
    getJobList(dispatch, {
      pageNum: pageNum,
      filters: searchState.filterParams,
    });
    return () => {
      dispatch({ type: jobList, payload: {} }); //unmount
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  //check code as well as message for get job list data
  useEffect(() => {
    if (jobListData?.code === 200) {
      setJobListState({
        isLoading: false,
        error: "",
        noRecords: jobListData?.data?.job?.length === 0,
        showLoaderOnSearch: false,
      });
    } else if (jobListData?.code || jobListData?.message) {
      setJobListState({
        isLoading: false,
        error:
          jobListData?.message || "Something went Wrong! Please try again.",
        noRecords: false,
        showLoaderOnSearch: false,
      });
    }
  }, [jobListData]);

  //get region ,paymentTerm, jobType list data
  useEffect(() => {
    let statusFilterJSON = [
      {
        field: "Status",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    getRegionList(dispatch);
    getJobTypeList(dispatch, { filters: JSON.stringify(statusFilterJSON) });
    return () => {
      dispatch({ type: regionList, payload: {} });
      dispatch({ type: jobTypeList, payload: {} });
    };
  }, [dispatch]);

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  //on row click encoded ID
  const handleRowOnClick = (rowData) => {
    const encodedId = urlEncodedId(rowData.ID);
    navigate(`/customer/jobs/${encodedId}/details`);
  };

  const handleScheduleChange = (dates) => {
    setScheduleDateRange(dates);
  };

  const [scheduleStart, scheduleEnd] = scheduleDateRange;

  const columns = [
    // { Header: "Sr.no.", accessor: "srNo", minWidth: 60 },
    {
      Header: `${WORK_ORDER} No.`,
      accessor: "ID",
      className: "col-job-no",
      Cell: ({ value, row }) => <span>{value ? value : "-"}</span>,
    },
    {
      Header: "Outlet Name",
      accessor: "customer",
      className: "col-customer-info",
      Cell: ({ row }) => {
        const { customerLocation } = row?.original;
        return (
          <div>
            <div className="d-flex align-items-center">
              <span>{customerLocation?.outletName || "-"}</span>
            </div>
          </div>
        );
      },
    },
    {
      Header: "Outlet Address",
      id: "address",
      accessor: (row) => row.customerLocation?.regionResponce?.name,
      className: "col-outlet-info",
      Cell: ({ row }) => {
        const { regionResponce } = row?.original?.customerLocation;
        return (
          <div>
            {/* Customer Name */}
            <div className="d-flex align-items-center mb-1">
              <i className="fa fa-location-dot mr-2 text-light" />
              <span>{row?.original?.customerLocation?.address || "-"}</span>
            </div>
            {/* Outlet Name from customer location column */}
            <div className="d-flex align-items-center">
              <i className="fa  fa-earth-oceania mr-2 text-light" />
              <span>{regionResponce?.name || "-"}</span>
            </div>
          </div>
        );
      },
    },
    {
      Header: "Contact Person Info",
      accessor: "customerLocation",
      className: "col-customer-info",
      Cell: ({ value }) => {
        const name = value?.contactPersonName || "-";
        const phone = value?.contactPersonPhone || "-";
        return (
          <div>
            <div className="d-flex align-items-center mb-1">
              <i className="fa fa-user mr-2 text-light" />
              <span>{name || "-"}</span>
            </div>
            <div className="d-flex align-items-center">
              <i className="fa fa-phone mr-2 text-light" />
              +966 {phone}
            </div>
          </div>
        );
      },
    },
    {
      Header: "Type",
      accessor: "jobTypeID",
      className: "col-job-type",
      Cell: ({ value }) => value?.name || "-",
    },

    {
      Header: "Assigned To",
      accessor: "technician",
      className: "col-assign-info",
      Cell: ({ value }) => value?.name || "-",
    },
    {
      Header: () => "Schedule Date",
      accessor: "scheduledFrom",
      className: "col-schedule-info",
      Cell: ({ row }) => {
        const { scheduledFrom, scheduledTo } = row?.original;

        return (
          <div>
            <div className="d-flex align-items-center mb-1">
              <strong className="mr-1">From:</strong>
              <span>{ISOToHumanReadableDate(scheduledFrom)}</span>
            </div>
            <div className="d-flex align-items-center">
              <strong className="mr-1">To:</strong>
              <span className="pl-3">
                {ISOToHumanReadableDate(scheduledTo)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      Header: "Status",
      accessor: "jobStatus",
      className: "col-job-status",
      Cell: ({ value }) => {
        const status = JOB_STATUS_OPTIONS.find(
          (item) => item.value === value
        );

        return (
          <Badge pill style={getJobStatusStyle(value)}>
            {status?.label || value}
          </Badge>
        );
      },
    },
    { Header: "Creation Date", accessor: "createdAt", className: "col-create-date" },
  ];

  const jobStatusColors = {
    // "Awaiting Payment": "warning",
    // "Awaiting Payment Approval": "warning",
    Cancelled: "danger",
    // "Collect Cash": "success",
    "Invoice (Not Paid)": "warning",
    "Invoice (Paid)": "success",
    "Job Allocated": "info",
    // "Pending Approval": "warning",
    "Quotation InProgress": "info",
    Repair: "info",
    Repaired: "success",
    "Request Approval": "secondary",
    "Request Parts": "secondary",
  };

  //on enter search
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };
  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState?.jobNo) {
      filterParams.push({
        field: "ID",
        condition: "eq",
        filterValues: [String(searchState.jobNo)],
      });
    }

    if (searchState?.region?.ID)
      filterParams.push({
        field: "CustomerRegionID",
        condition: "eq",
        filterValues: [String(searchState.region.ID)],
      });

    if (searchState?.jobType?.ID) {
      filterParams.push({
        field: "JobTypeID",
        condition: "eq",
        filterValues: [String(searchState.jobType.ID)],
      });
    }

    if (searchState?.status?.value)
      filterParams.push({
        field: "JobStatus",
        condition: "eq",
        filterValues: [searchState.status.value],
      });

    //schedule date (btw)
    if (scheduleStart && scheduleEnd) {
      filterParams.push({
        field: "ScheduledTo",
        condition: "btw",
        filterValues: [
          formatDateLocal(scheduleStart),
          formatDateLocal(scheduleEnd),
        ],
      });
    }
    if (scheduleStart && !scheduleEnd) {
      filterParams.push({
        field: "ScheduledTo",
        condition: "btw",
        filterValues: [
          formatDateLocal(scheduleStart),
          formatDateLocal(scheduleStart)
        ],
      });
    }
    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setSearchState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
        isSearched: true,
      }));
      setJobListState((prev) => ({
        ...prev,
        showLoaderOnSearch: true,
        isLoading: true,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const clearSearch = () => {
    setSearchState({
      status: null,
      region: null,
      jobType: null,
      jobNo: "",
      filterParams: [],
      isSearched: false,
    });
    //reset schedule and creation
    setScheduleDateRange([null, null]);
    setJobListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    if (pageNum !== 1) setPageNum(1);
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge color="" pill style={getJobStatusStyle(props.data.value)}>
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <Badge color="" pill style={getJobStatusStyle(props.data.value)}>
        {props.data.label}
      </Badge>
    </components.SingleValue>
  );

  return (
    <>
      {isDownloading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Processing… Please wait.
            </div>
          </div>
        </div>
      )}
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between mb-2">
                <CardTitle tag="h3" className="font-weight-normal">
                  {WORK_ORDER} Requests
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="d-flex flex-wrap align-items-center justify-content-start filter-group">
                  <Input
                    type="text"
                    placeholder={`Search by ${SMALL_CASE_WORK_ORDER} no.`}
                    className="mr-2 mb-2 filter-input-small"
                    value={searchState.jobNo}
                    onChange={(e) =>
                      setSearchState((prev) => ({
                        ...prev,
                        jobNo: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                    maxLength={100}
                  />

                  <Select
                    className="mr-2 mb-2 filter-select"
                    options={jobTypeListData?.data?.jobType}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.ID}
                    value={searchState.jobType}
                    placeholder={`${CAP_CASE_WORK_ORDER} type`}
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle(true)}
                    onChange={(selected) =>
                      setSearchState((prev) => ({
                        ...prev,
                        jobType: selected,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <Select
                    className="mr-2 mb-2 filter-select"
                    options={regionListData?.data?.region}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.ID}
                    value={searchState.region}
                    placeholder="Region"
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle(true)}
                    onChange={(selected) =>
                      setSearchState((prev) => ({
                        ...prev,
                        region: selected,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <Select
                    className="mb-2 filter-select"
                    options={JOB_STATUS_OPTIONS}
                    value={searchState.status}
                    placeholder="Status"
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle(true)}
                    components={{ Option, SingleValue }}
                    onChange={(selected) =>
                      setSearchState((prev) => ({
                        ...prev,
                        status: selected,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <div className="d-flex align-items-center date-picker-style">
                    <DatePicker
                      selected={scheduleStart}
                      onChange={handleScheduleChange}
                      startDate={scheduleStart}
                      endDate={scheduleEnd}
                      selectsRange
                      isClearable
                      placeholderText="Select schedule date or range"
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      onKeyDown={(e) => e.preventDefault()}
                    />
                  </div>
                  <Button
                    color="default"
                    className="d-flex justify-content-center align-items-center mb-2 mt-0 filter-btn btn-simple"
                    onClick={handleSearch}
                  >
                    {jobListState.showLoaderOnSearch ? (
                      <>
                        <i className="fa fa-spinner fa-spin search-loader" />{" "}
                      </>
                    ) : (
                      <>
                        <i className="fa fa-search mr-1" /> Search
                      </>
                    )}
                  </Button>
                  {(searchState.filterParams.length > 0 ||
                    searchState.isSearched) && (
                      <Button
                        color="default"
                        className="d-flex justify-content-center align-items-center mt-0 mb-2 ml-2 filter-btn btn-simple"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                </div>

                <div className="mt-3 mb-3 mx-1">
                  {jobListData?.data?.totalRecords ? (
                    searchState.filterParams &&
                      Object.keys(searchState.filterParams).length > 0 ? (
                      <span className="font-weight-bold">
                        Showing {jobListData?.data?.totalFilteredRecords} of{" "}
                        {jobListData?.data?.totalRecords} {WORK_ORDER}s
                      </span>
                    ) : (
                      <span className="font-weight-bold">
                        Total {WORK_ORDER}s: {jobListData?.data?.totalRecords}
                      </span>
                    )
                  ) : null}

                  {/* totalfilterRecords display is remaining  */}
                </div>
                {jobListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : jobListState.error ? (
                  <>
                    <div className="loaderContainer">{jobListState.error}</div>
                  </>
                ) : jobListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    {searchState.isSearched
                      ? "No records found for your search."
                      : `No ${SMALL_CASE_WORK_ORDER} added yet.`}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      showPaginationBottom={true}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE || 25}
                      activePage={pageNum}
                      data={formattedJobList || []}
                      rowClick={handleRowOnClick}
                      resizable={false}
                      columns={columns}
                      className="-striped -highlight table-hover table-fixed" // Add table-fixed class
                      totalPages={Math.ceil(
                        jobListData?.data?.totalFilteredRecords /
                        jobListData?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE,
                      )}
                      setPageNum={setPageNum}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default JobsListing;
